import {createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {Alternative} from "../../../../api/QuoteAlternatives";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useGetAll} from "../../../logistics/routes/useGetAll";
import {api} from "../../../../api/API";
import {useAlternative} from "./Alternative";

export const AlternativeContext = createContext({
    valid: false,
    alternative: new EventEmitter<Alternative|undefined>(),
    alternatives: new EventEmitter<Alternative[]>(),
    reloadEmitter: new EventEmitter<number>(),
    reload: () => {},
});

export function AlternativeProvider(props: PropsWithChildren<{}>) {

    const ctx = useMemo(() => {
        const reloadEmitter = new EventEmitter<number>();

        return (
            {
                valid: true,
                alternative: new EventEmitter<Alternative|undefined>(),
                alternatives: new EventEmitter<Alternative[]>(),
                reloadEmitter: reloadEmitter,
                reload: () => reloadEmitter.emit(Date.now()),
            }
        )
    }, []);

    return (
        <AlternativeContext.Provider value={ctx}>
            {props.children}
            <AlternativeLoader />
        </AlternativeContext.Provider>
    )
}

function AlternativeLoader() {
    const ctx = useContext(AlternativeContext);

    const project = useProjectId();
    const altId = useAlternative();

    const list = useGetAll(ctx => api.quoteAlternatives.list(ctx),
        {
            project: project,
        }, [project], {useLazy: true});

    const result = list.asList;
    const reloadRef = useRef(list.reload);
    reloadRef.current = list.reload;
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;

    useEffect(() => {
        const sub = ctxRef.current.reloadEmitter.subscribe(() => reloadRef.current())
        return () => sub.cancel();
    }, [])

    useEffect(() => {
        ctxRef.current.alternatives.emit(result);
    }, [result]);

    useEffect(() => {
        const alt = result.find(r => r.id === altId);
        ctxRef.current.alternative.emit(alt);
    }, [altId, result]);

    return null;
}

export function useAlternative2() {
    const ctx = useContext(AlternativeContext);
    const altEmitter = ctx.alternative;

    const [value, setValue] = useState<Alternative|undefined>(altEmitter.lastValue);

    useEffect(() => {
        const sub = altEmitter.subscribeAndFireLast(setValue);
        return () => sub.cancel();
    }, [altEmitter]);

    return value;
}